import { createTheme, Theme } from '@mui/material/styles';
import themeConfig from './themeConfig';
import themePalette from './palette';
import themeTypography from './typography';
import { PaletteMode } from '@mui/material';
import { COLOR_KEY, getModeColor } from './customization.value';

const color = themeConfig.colors;

const themeOption = {
    // mode: 'light',
    colors: color,
    heading: color.grey900,
    paper: color.paper,
    paperDark: color.paperDark,
    backgroundDefault: color.paper,
    backgroundDark: color.paperDark,
    background: color.primaryLight,
    TextPrimary: color.textColor,
    darkTextPrimary: color.textColorDark,
    darkTextSecondary: color.secondaryTextColorDark,
    textSecondary: color.secondaryTextColor,
    textDark: color.grey900,
    menuSelected: color.secondaryDark,
    menuSelectedBack: color.secondaryLight,
    divider: color.grey200,
    fontFamily: themeConfig.fontFamily,
    borderRadius: themeConfig.borderRadius,
    // customization
};
export type ThemeOption = typeof themeOption & { mode?: PaletteMode };

/**
 * Represent theme style and structure as per Material-UI
 * @param {JsonObject} customization customization parameter object
 */
export function theme(mode?: PaletteMode) {

    const AthemeOption = { ...themeOption, mode }
    const getColor = (key: COLOR_KEY) => getModeColor(key, mode || 'light');

    return createTheme({
        spacing: 4,
        direction: 'ltr',
        palette: themePalette(AthemeOption),
        mixins: {
            toolbar: {
                minHeight: '48px',
                padding: '16px',
                '@media (min-width: 600px)': {
                    minHeight: '48px'
                }
            }
        },
        breakpoints: {
            values: {
                xs: 0,
                sm: 600,
                md: 960,
                lg: 1280,
                // 原1980
                xl: 1536
            }
        },
        // 暂时注释，使用默认配置
        typography: themeTypography,
        components: {
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        margin: '0rem 0.5rem 0.25rem',
                        padding: '0.4375rem 1rem',
                        borderRadius: '6px',
                        '&.Mui-selected': {
                            backgroundColor: AthemeOption.colors.primaryMain + '!important',
                            color: AthemeOption.colors.contrastText + '!important'
                        },
                        '&:hover': {
                            backgroundColor: getColor(COLOR_KEY.iconPrimary300),
                            color: getColor(COLOR_KEY.iconNeutral100),
                        },
                    }
                }
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        margin: '0rem 0.5rem 0.25rem',
                        padding: '0.4375rem 1rem',
                        borderRadius: '6px',
                        '&.Mui-selected': {
                            backgroundColor: AthemeOption.colors.primaryMain + '!important',
                            color: AthemeOption.colors.contrastText + '!important',
                        },
                        '&:hover': {
                            backgroundColor: getColor(COLOR_KEY.iconPrimary300),
                            color: getColor(COLOR_KEY.iconNeutral100),
                        },
                    }
                }
            },
            /**
             * Listbox 内嵌样式对 `.MuiAutocomplete-option.Mui-focused` 使用 `action.hover`（键盘/逻辑高亮）。
             * 若把 `.Mui-focused` 也做成紫底，会与 `MenuItem` 的 `:hover` 紫底叠加，出现「第一项 + 当前悬停项」双紫
             *（autoHighlight 时第一项常保持 focused）。
             * 因此：focused = 灰底 + 正文色；紫底白字仅由 `:hover` / 选中态承担。
             */
            MuiAutocomplete: {
                styleOverrides: {
                    listbox: ({ theme }: { theme: Theme }) => ({
                        '& .MuiAutocomplete-option.Mui-focused': {
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.text.primary,
                            '@media (hover: none)': {
                                backgroundColor: 'transparent',
                            },
                        },
                        '& .MuiAutocomplete-option.Mui-focusVisible': {
                            backgroundColor: theme.palette.action.focus,
                            color: theme.palette.text.primary,
                        },
                        '& .MuiAutocomplete-option.Mui-focused:hover': {
                            backgroundColor: getColor(COLOR_KEY.iconPrimary300),
                            color: getColor(COLOR_KEY.iconNeutral100),
                        },
                        '& .MuiAutocomplete-option[aria-selected="true"]': {
                            backgroundColor: AthemeOption.colors.primaryMain + '!important',
                            color: AthemeOption.colors.contrastText + '!important',
                        },
                        '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
                            backgroundColor: AthemeOption.colors.primaryMain + '!important',
                            color: AthemeOption.colors.contrastText + '!important',
                        },
                    }),
                    option: {
                        margin: '0rem 0.5rem 0.25rem',
                        padding: '0.4375rem 1rem',
                        borderRadius: '6px',
                        '&[aria-selected="true"]': {
                            backgroundColor: AthemeOption.colors.primaryMain + '!important',
                            color: AthemeOption.colors.contrastText + '!important',
                        },
                        '&:hover': {
                            backgroundColor: getColor(COLOR_KEY.iconPrimary300),
                            color: getColor(COLOR_KEY.iconNeutral100),
                        },
                    },
                },
            },
        }
    });
}

export default theme;
